import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, Package, CreditCard, TrendingUp, AlertCircle, RefreshCw, BarChart2 } from 'lucide-react';
import apiClient from '../../utils/api';

const DATE_PRESETS = [
  { label: 'All Time', value: '' },
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
];

const shipmentDestination = (shipment: any) => {
  const latestTracking = Array.isArray(shipment.tracking) ? shipment.tracking[0] : null;
  const parts = [
    shipment.receiverAddress,
    shipment.receiverCity,
    shipment.departmentBranch?.name ? `Branch: ${shipment.departmentBranch.name}` : null,
  ].filter(Boolean);

  return {
    label: parts.join(', ') || '—',
    location: latestTracking?.location,
  };
};

function getDateRange(preset: string): { dateFrom?: string; dateTo?: string } {
  if (!preset) return {};
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  if (preset === 'today') return { dateFrom: fmt(now), dateTo: fmt(now) };
  const days = Number.parseInt(preset, 10);
  if (!Number.isNaN(days)) {
    const from = new Date(now); from.setDate(from.getDate() - days);
    return { dateFrom: fmt(from), dateTo: fmt(now) };
  }
  return {};
}

export const SuperAdminDashboard: React.FC = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [companyBreakdown, setCompanyBreakdown] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange(datePreset);
      const [c, s, u, cb] = await Promise.all([
        apiClient.listCompanies(100, 0),
        apiClient.getDashboardStats(dateRange),
        apiClient.listUsers(undefined, 100, 0),
        apiClient.getCompaniesBreakdown(dateRange),
      ]);
      const cd = c.data.data; setCompanies(cd?.companies ?? cd?.rows ?? (Array.isArray(cd) ? cd : []));
      setStats(s.data.data);
      const ud = u.data.data; setUsers(ud?.users ?? ud?.rows ?? (Array.isArray(ud) ? ud : []));
      setCompanyBreakdown(Array.isArray(cb.data.data) ? cb.data.data : []);
    } catch {} finally { setLoading(false); }
  }, [datePreset]);

  useEffect(() => { load(); }, [load]);

  const activeCompanies = companies.filter(c => c.status === 'ACTIVE').length;
  const s = stats?.shipments;
  const pendingPayments = stats?.payments?.pending?.count || 0;
  const totalRevenue = stats?.payments?.completed?.total || 0;

  const statCards = [
    { label: 'Total Companies', value: companies.length, sub: `${activeCompanies} active`, icon: <Building2 size={20} />, color: '#6366f1', link: '/super-admin/companies' },
    { label: 'Total Users', value: users.length, sub: 'across all companies', icon: <Users size={20} />, color: '#0ea5e9', link: '/super-admin/users' },
    { label: 'Total Shipments', value: s?.total ?? 0, sub: `${s?.inTransit ?? 0} in transit`, icon: <Package size={20} />, color: '#f59e0b', link: '/super-admin/shipments' },
    { label: 'Pending Payments', value: pendingPayments, sub: 'awaiting collection', icon: <CreditCard size={20} />, color: '#ef4444', link: '/super-admin/payments' },
    { label: 'Delivery Rate', value: `${s?.deliveryRate ?? 0}%`, sub: 'of all shipments', icon: <TrendingUp size={20} />, color: '#22c55e', link: '/super-admin/shipments' },
    { label: 'Revenue (RWF)', value: totalRevenue.toLocaleString(), sub: 'completed payments', icon: <CreditCard size={20} />, color: '#10b981', link: '/super-admin/payments' },
  ];

  // Top 5 companies by shipment volume
  const topCompanies = [...companyBreakdown].sort((a, b) => b.totalShipments - a.totalShipments).slice(0, 5);
  const maxShipments = Math.max(...topCompanies.map(c => c.totalShipments), 1);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">System Overview</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={datePreset} onChange={e => setDatePreset(e.target.value)} style={{ width: 160 }}>
            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button className="btn btn-ghost" onClick={load} disabled={loading}><RefreshCw size={14} /></button>
          <Link to="/super-admin/companies/new" className="btn btn-primary">
            <Building2 size={16} /> New Company
          </Link>
        </div>
      </div>

      {loading ? <div className="loading-spinner">Loading system data...</div> : (
        <>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {statCards.map(stat => (
              <Link to={stat.link} key={stat.label} style={{ textDecoration: 'none' }}>
                <div className="stat-card" style={{ borderLeft: `4px solid ${stat.color}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div className="stat-label">{stat.label}</div>
                      <div className="stat-value" style={{ color: stat.color, fontSize: typeof stat.value === 'string' && stat.value.length > 8 ? 18 : 28 }}>{stat.value}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{stat.sub}</div>
                    </div>
                    <div style={{ color: stat.color, opacity: 0.6 }}>{stat.icon}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {(s?.pending ?? 0) > 0 && (
            <div className="card" style={{ background: '#fef9c3', border: '1px solid #fde047', marginBottom: 20, marginTop: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#854d0e' }}>
                <AlertCircle size={18} />
                <span><strong>{s.pending}</strong> shipments awaiting confirmation.</span>
                <Link to="/super-admin/shipments?status=PENDING" className="btn btn-ghost" style={{ marginLeft: 'auto', fontSize: 12 }}>Review</Link>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
            {/* Top companies by volume */}
            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BarChart2 size={15} /> Top Companies by Volume
                </h2>
                <Link to="/super-admin/reports" className="btn btn-ghost" style={{ fontSize: 12 }}>Full Report</Link>
              </div>
              {topCompanies.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 13 }}>No data</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {topCompanies.map(c => (
                    <div key={c.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <Link to={`/super-admin/companies/${c.id}`} style={{ fontWeight: 600, color: '#374151' }}>{c.name}</Link>
                        <span style={{ color: '#64748b' }}>{c.totalShipments} shipments · {c.deliveryRate}% delivered</span>
                      </div>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(c.totalShipments / maxShipments) * 100}%`, background: 'linear-gradient(90deg, #3b82f6, #6366f1)', borderRadius: 3 }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent shipments */}
            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>Recent Shipments</h2>
                <Link to="/super-admin/shipments" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Tracking #</th><th>Receiver</th><th>Delivery Location</th><th>Status</th></tr></thead>
                  <tbody>
                    {(stats?.recentShipments ?? []).map((s: any) => {
                      const destination = shipmentDestination(s);
                      return (
                        <tr key={s.id}>
                          <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                          <td>
                            <div style={{ fontWeight: 500 }}>{s.receiverName || '—'}</div>
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Phone: {s.receiverPhone || '—'}</div>
                          </td>
                          <td>
                            <div style={{ fontSize: 12, color: '#334155' }}>{destination.label}</div>
                            {destination.location && destination.location !== destination.label && (
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Location: {destination.location}</div>
                            )}
                          </td>
                          <td><span className={`badge badge-${s.status?.toLowerCase().replaceAll('_', '-')}`}>{s.status}</span></td>
                        </tr>
                      );
                    })}
                    {(stats?.recentShipments ?? []).length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No shipments</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Companies table */}
          <div className="card" style={{ marginTop: 20 }}>
            <div className="page-header" style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600 }}>Companies</h2>
              <Link to="/super-admin/companies" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
            </div>
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Name</th><th>City</th><th>Shipments</th><th>Revenue (RWF)</th><th>Delivery Rate</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {companyBreakdown.slice(0, 8).map((c: any) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 500 }}>{c.name}</td>
                      <td>{c.city || 'Rwanda'}</td>
                      <td>{c.totalShipments}</td>
                      <td>{c.revenue.toLocaleString()}</td>
                      <td><span style={{ color: c.deliveryRate >= 80 ? '#22c55e' : c.deliveryRate >= 50 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>{c.deliveryRate}%</span></td>
                      <td><span className={`badge badge-${c.status?.toLowerCase()}`}>{c.status}</span></td>
                      <td><Link to={`/super-admin/companies/${c.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ marginTop: 20 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Quick Actions</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'Create Company', to: '/super-admin/companies/new', icon: <Building2 size={15} /> },
                { label: 'Create User', to: '/super-admin/users/new', icon: <Users size={15} /> },
                { label: 'All Shipments', to: '/super-admin/shipments', icon: <Package size={15} /> },
                { label: 'All Payments', to: '/super-admin/payments', icon: <CreditCard size={15} /> },
                { label: 'Reports', to: '/super-admin/reports', icon: <BarChart2 size={15} /> },
                { label: 'Audit Log', to: '/super-admin/audit-log', icon: <TrendingUp size={15} /> },
              ].map(a => (
                <Link key={a.label} to={a.to} className="btn btn-ghost">{a.icon} {a.label}</Link>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
